import * as React from "react";
import { useState, useEffect } from "react";
import { JobCardProps } from "./types";
import { useTooltip } from "./contexts/TooltipContext";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";

// Custom Tooltip content component for Recharts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-300 rounded-md shadow-lg text-sm">
        <p className="font-semibold text-gray-800">{`Year: ${label}`}</p>
        <p className="text-blue-600">{`Jobs: ${payload[0].value.toLocaleString()}`}</p>
      </div>
    );
  }
  return null;
};

// Helper functions for growth percentage formatting
const formatGrowthPercentage = (percentage: number | null | undefined): string => {
  if (percentage === null || percentage === undefined) return 'Not available';
  
  // Cap extreme percentages to prevent unrealistic displays
  const cappedPercentage = Math.max(-99, Math.min(99, percentage));
  
  // Format based on magnitude
  if (Math.abs(cappedPercentage) < 0.1) {
    return '0%';
  } else if (Math.abs(cappedPercentage) < 1) {
    return `${cappedPercentage > 0 ? '+' : ''}${cappedPercentage.toFixed(1)}%`;
  } else {
    return `${cappedPercentage > 0 ? '+' : ''}${Math.round(cappedPercentage)}%`;
  }
};

const getGrowthTooltip = (percentage: number | null | undefined, current: number | null | undefined, projected: number | null | undefined): string => {
  if (percentage === null || percentage === undefined) return 'Growth data not available';
  
  const currentFormatted = current ? current.toLocaleString() : 'N/A';
  const projectedFormatted = projected ? projected.toLocaleString() : 'N/A';
  
  if (Math.abs(percentage) > 99) {
    return `Actual growth: ${percentage > 0 ? '+' : ''}${Math.round(percentage)}% (capped for display)\n2023: ${currentFormatted} → 2026: ${projectedFormatted}`;
  }
  
  return `2023: ${currentFormatted} → 2026: ${projectedFormatted}\nGrowth: ${percentage > 0 ? '+' : ''}${Math.round(percentage)}%`;
};

export function JobCard({ job, onCardClick, ...props }: JobCardProps & { 'data-first'?: string }) {
  // Tooltip context for global tooltip state
  const { setIsTooltipOpen } = useTooltip();
  
  const [isDemoTourActive, setIsDemoTourActive] = useState(false);
  const [isFirstJobCard, setIsFirstJobCard] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [showMedianSalary, setShowMedianSalary] = useState(false);
  const [showColTooltip, setShowColTooltip] = useState(false);
  const [windowSize, setWindowSize] = useState(6);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if demo tour is active and if this is the first job card
  useEffect(() => {
    const checkDemoTour = () => {
      // Check if this is the first job card by checking the data-first attribute
      const isFirst = props['data-first'] === 'true';
      setIsFirstJobCard(isFirst);
    };

    // Check initially
    checkDemoTour();

    // Listen for tour state events
    const handleTourStarted = () => {
      setIsDemoTourActive(true);
      setHasBeenClicked(false);
    };

    const handleTourEnded = () => {
      setIsDemoTourActive(false);
      setHasBeenClicked(false);
    };

    // Listen for tour-triggered clicks (only for first job card)
    const handleTourClick = () => {
      // During tour, any job card can be clicked
      if (isDemoTourActive && !hasBeenClicked) {
        setHasBeenClicked(true);
        
        // During tour, trigger the click immediately
        onCardClick();
      }
    };

    window.addEventListener('tourStarted', handleTourStarted);
    window.addEventListener('tourEnded', handleTourEnded);
    window.addEventListener('tourClickJobCard', handleTourClick);

    return () => {
      window.removeEventListener('tourStarted', handleTourStarted);
      window.removeEventListener('tourEnded', handleTourEnded);
      window.removeEventListener('tourClickJobCard', handleTourClick);
    };
  }, [isDemoTourActive, isFirstJobCard, onCardClick, props['data-first'], job.title, hasBeenClicked]);

  // Close tooltip when pressing Escape or clicking outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowColTooltip(false);
        setIsTooltipOpen(false);
      }
    };
    if (showColTooltip) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showColTooltip]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the info button or the tooltip itself
      if (target.closest('[aria-label*="Salary Information"]') || 
          target.closest('.col-salary-tooltip')) {
        return;
      }
      setShowColTooltip(false);
      setIsTooltipOpen(false);
    };
    if (showColTooltip) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showColTooltip]);

  const parseCurrencyValue = (value?: string | number | null): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseCurrencyRange = (value?: string | null): { min: number; max: number } | null => {
    if (!value) return null;
    const normalized = value.replace(/–/g, "-");
    if (!normalized.includes("-")) return null;
    const [minPart, maxPart] = normalized.split("-");
    const min = parseCurrencyValue(minPart);
    const max = parseCurrencyValue(maxPart);
    if (min !== null && max !== null && min > 0 && max > 0) {
      return { min, max };
    }
    return null;
  };

  const formatCurrencyRange = (min?: number | null, max?: number | null): string | null => {
    if (
      min === null ||
      max === null ||
      min === undefined ||
      max === undefined ||
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      min <= 0 ||
      max <= 0
    ) {
      return null;
    }
    return `$${Math.round(min).toLocaleString()} - $${Math.round(max).toLocaleString()}`;
  };

  // Format salary and wage values to remove decimals
  const formatSalary = (salary?: string | number) => {
    if (!salary) return 'Not available';
    
    // If it's already a formatted string with dollar sign, just return it
    if (typeof salary === 'string' && salary.includes('$')) {
      return salary;
    }
    
    const numericSalary = parseCurrencyValue(salary);
    if (numericSalary === null || numericSalary <= 0) return 'Not available';
    
    return `$${Math.round(numericSalary).toLocaleString()}`;
  };

  const formatWage = (wage?: string | number) => {
    if (!wage) return 'Not available';
    
    // If it's already a formatted string with dollar sign, just return it
    if (typeof wage === 'string' && wage.includes('$')) {
      return wage;
    }
    
    const numericWage = parseCurrencyValue(wage);
    if (numericWage === null || numericWage <= 0) return 'Not available';
    
    return `$${Math.round(numericWage).toLocaleString()}`;
  };

  // Get hourly wage range from 2023 non-COL percentile data (10th-90th percentile)
  const getHourlyRange = () => {
    if (job.hourlyRange10to90) {
      return job.hourlyRange10to90;
    }

    const hourlyMin = parseCurrencyValue(job.hourlyPct10);
    const hourlyMax = parseCurrencyValue(job.hourlyPct90);
    
    if (
      hourlyMin !== null &&
      hourlyMax !== null &&
      hourlyMin > 0 &&
      hourlyMax > 0
    ) {
      return `$${Math.round(hourlyMin).toLocaleString()} - $${Math.round(hourlyMax).toLocaleString()}`;
    }
    
    const formattedWage = formatWage(job.hourlyWage);
    if (formattedWage !== 'Not available') {
      const numericFormatted = parseCurrencyValue(formattedWage);
      if (numericFormatted && numericFormatted > 0) {
        return formattedWage;
      }
    }
    
    return 'Not available';
  };

  // Get annual salary range from 2023 non-COL percentile data (10th-90th percentile)
  const getAnnualSalaryRange = () => {
    if (job.salaryRange10to90) {
      return job.salaryRange10to90;
    }

    const annualMin = parseCurrencyValue(job.salaryPct10);
    const annualMax = parseCurrencyValue(job.salaryPct90);

    if (
      annualMin !== null &&
      annualMax !== null &&
      annualMin > 0 &&
      annualMax > 0
    ) {
      const range = formatCurrencyRange(annualMin, annualMax);
      if (range) {
        return range;
      }
    }
    
    if (job.salaryRange && !job.salaryRange.toLowerCase().includes('not')) {
      const range = parseCurrencyRange(job.salaryRange);
      if (range) {
        const formatted = formatCurrencyRange(range.min, range.max);
        if (formatted) {
          return formatted;
        }
      }
      return job.salaryRange;
    }
    
    const formattedSalary = formatSalary(job.salary);
    if (formattedSalary !== 'Not available') {
      return formattedSalary;
    }
    
    const formattedEstimated = formatSalary(job.estimatedSalary);
    if (formattedEstimated !== 'Not available') {
      return formattedEstimated;
    }
    
    return 'Not available';
  };

  const capitalizeTitle = (title: string) => {
    return title
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  // Create dynamic job data based on current year and selected window
  const createJobData = () => {
    const currentYear = new Date().getFullYear();
    const baseYear = currentYear - 1; // Start from previous year (e.g., 2025 -> 2024)
    const endYear = baseYear + windowSize - 1; // End year based on window size (6 years = 6 data points)
    const jobData = [];
    
    // Get data for the dynamic range: baseYear to endYear
    for (let year = baseYear; year <= endYear; year++) {
      const openingsKey = `openings${year}` as keyof typeof job;
      const openings = job[openingsKey];
      
      // Only include years that have data (not null, undefined, or 0)
      if (openings !== null && openings !== undefined && openings !== 0) {
        // Ensure openings is a number
        const numericOpenings = typeof openings === 'number' ? openings : Number(openings) || 0;
        if (numericOpenings > 0) {
          jobData.push({ year, jobs: numericOpenings });
        }
      }
    }
    
    return jobData;
  };

  const jobData = createJobData();
  const windowedData = jobData; // Use all data from the dynamic window
  const start = windowedData[0];
  const end = windowedData[windowedData.length - 1];
  
  // Calculate year range for display
  const currentYear = new Date().getFullYear();
  const baseYear = currentYear - 1;
  const endYear = baseYear + windowSize - 1;
  
  // Handle case when no job data is available
  const hasJobData = windowedData.length > 0 && start && end;

  const svgPadX = 7;
  const svgPadY = 12;
  const chartW = 100 - svgPadX * 2;
  const chartH = 100 - svgPadY - 8;
  const getX = (i: number) =>
    svgPadX + (i / (windowedData.length - 1)) * chartW;
  const getY = (jobs: number) =>
    svgPadY + (1 - jobs / Math.max(...jobData.map((d) => d.jobs || 0), 1)) * chartH;

  const handleCardClick = () => {
    // Allow clicking during tour, but let the tour handler manage the click
    if (!isDemoTourActive) {
      onCardClick();
    }
  };

  return (
    <article
      className={`p-4 bg-white rounded-xl border-2 border-gray-200 border-solid transition-all duration-[0.2s] ease-[ease] hover:shadow-lg hover:border-sky-300 job-card important relative ${
        isDemoTourActive ? 'cursor-default opacity-75' : 'cursor-pointer'
      }`}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${job.title} position`}
      onClick={handleCardClick}
      data-first={props['data-first']}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      <header className="mb-2">
        <h3 
          className="mx-0 mt-0 mb-1 text-lg font-medium leading-tight text-zinc-700 cursor-help"
          title="Official job title and classification"
        >
          {job.oohUrl ? (
            <a
              href={job.oohUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sky-600 transition-colors duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {capitalizeTitle(job.title)}
            </a>
          ) : (
            capitalizeTitle(job.title)
          )}
        </h3>
        <p 
          className="m-0 text-sm font-medium opacity-60 text-zinc-700 cursor-help"
          title="Standard Occupational Classification code for this job"
        >
          SOC Code: {job.socCode}
        </p>
        
        {/* Show alternate title match if found via alternate title search */}
        {job.matched_via_alternate_title && job.matching_alternate_titles && job.matching_alternate_titles.length > 0 && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-medium text-blue-800 mb-1">
              Found via alternate title:
            </p>
            {job.matching_alternate_titles.map((match, index) => (
              <p key={index} className="text-sm text-blue-700 font-medium">
                "{match.matched_alternate_title}"
              </p>
            ))}
            <p className="text-xs text-blue-600 mt-1">
              → Corresponding O*NET title: {job.title}
            </p>
          </div>
        )}
      </header>

      {/* -------- Tags Section -------- */}
      <div className="mb-2">
        <span 
          className="inline-block px-2 py-1 mr-2 mb-1 text-xs font-medium text-white bg-sky-600 rounded-md cursor-help"
          title="Industry sector this job belongs to"
        >
          {job.sector}
        </span>
        <span 
          className="inline-block px-2 py-1 mr-2 mb-1 text-xs font-medium text-white bg-green-600 rounded-md cursor-help"
          title="Career cluster classification for this job"
        >
          {job.careerCluster || "Other"}
        </span>
        <span 
          className="inline-block px-2 py-1 text-xs font-medium text-white bg-yellow-500 rounded-md cursor-help"
          title="Specific focus area or specialization"
        >
          {job.focusArea}
        </span>
      </div>

      {/* -------- Education -------- */}
      <div className="mb-2">
        <p 
          className="text-sm font-medium text-zinc-700 cursor-help"
          title="Minimum education requirement for this job"
        >
          Required Education:
        </p>
        <p 
          className="text-sm opacity-80 text-zinc-700 cursor-help"
          title="Educational background needed to qualify for this position"
        >
          {job.education}
        </p>
      </div>

      {/* -------- Salary Section -------- */}
      <div className="grid gap-3 p-3 mb-2 bg-blue-50 rounded-lg border border-gray-200 grid-cols-2 salary-section">
          <div>
            <div className="flex items-center justify-between mb-1" style={{ position: 'relative' }}>
              <div className="flex items-center gap-1">
              <p 
                className="text-xs font-medium uppercase text-zinc-700 flex items-center gap-1"
              >
                {showMedianSalary ? 'Annual Salary' : 'Annual Salary Range'}
                {!isMobile && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center cursor-pointer border-0 bg-transparent p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowColTooltip(!showColTooltip);
                      setIsTooltipOpen(!showColTooltip);
                    }}
                    onMouseEnter={() => {
                      setShowColTooltip(true);
                      setIsTooltipOpen(true);
                    }}
                    onMouseLeave={() => {
                      setShowColTooltip(false);
                      setIsTooltipOpen(false);
                    }}
                    onFocus={() => {
                      setShowColTooltip(true);
                      setIsTooltipOpen(true);
                    }}
                    onBlur={() => {
                      setShowColTooltip(false);
                      setIsTooltipOpen(false);
                    }}
                    aria-label={showMedianSalary ? "Median Salary Information" : "Salary Range Information"}
                    tabIndex={0}
                  >
                    <svg style={{ width: '12px', height: '12px', color: '#d5a32a' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </p>
              {/* Desktop Tooltip */}
              {showColTooltip && !isMobile && (
                <div 
                  className="col-salary-tooltip absolute left-0 top-full mt-2 z-[99999] w-72 p-3 bg-white border-2 border-[#d5a32a] rounded-lg shadow-xl text-xs text-gray-700"
                  style={{ whiteSpace: 'normal' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseEnter={() => setShowColTooltip(true)}
                  onMouseLeave={() => setShowColTooltip(false)}
                >
                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#1b2c5a' }}>
                    {showMedianSalary ? 'Cost of Living (COL) Salary' : 'Salary Range (10th–90th percentile)'}
                  </strong>
                  <p style={{ margin: 0, lineHeight: '1.5' }}>
                    {showMedianSalary 
                      ? 'This number shows what a typical paycheck is worth in Sumner County after adjusting for local costs, so you can see how far your money really goes here compared to other places.'
                      : 'This range shows salaries from the 10th percentile (lowest earners) to 90th percentile (highest earners) in Sumner County.'}
                  </p>
                  <div className="absolute -top-2 left-4 w-3 h-3 bg-white border-t-2 border-l-2 border-[#d5a32a] transform rotate-45"></div>
                </div>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMedianSalary(!showMedianSalary);
              }}
              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 ${
                showMedianSalary ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              title={`Toggle between ${showMedianSalary ? 'Salary Range' : 'Median Salary'} view`}
            >
              <span
                className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                  showMedianSalary ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          <p 
            className="text-base font-semibold text-primary cursor-help" 
            title={showMedianSalary ? "Median annual salary for Sumner County" : "Typical salary range from lowest to highest earners in Sumner County"}
          >
            {showMedianSalary ? formatSalary(job.salary) : getAnnualSalaryRange()}
          </p>
          
          {/* Mobile: Tappable Salary Explanation */}
          {isMobile && (
            <button
              type="button"
                onClick={(e) => {
                e.stopPropagation();
                setShowColTooltip(true);
                setIsTooltipOpen(true);
              }}
              className="mt-2 w-full text-left px-2 py-1.5 bg-white border border-[#d5a32a] rounded-lg flex items-center gap-2 hover:bg-[#fff8e1] transition-colors"
            >
              <svg className="w-3.5 h-3.5 text-[#d5a32a] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-[#1b2c5a] font-medium">
                {showMedianSalary ? 'Cost of living explained' : 'What is salary range?'}
              </span>
            </button>
          )}
        </div>
        <div>
          <p 
            className="text-xs font-medium uppercase text-zinc-700 mb-1 cursor-help"
            title={showMedianSalary ? "Median hourly wage for this position in Sumner County" : "Typical hourly wage range from lowest to highest earners"}
          >
            {showMedianSalary ? 'Hourly Wage' : 'Hourly Wage Range'}
          </p>
          <p 
            className="text-base font-semibold text-primary cursor-help" 
            title={showMedianSalary ? "Median hourly wage for Sumner County" : "Typical hourly wage range from lowest to highest earners in Sumner County"}
          >
            {showMedianSalary ? formatWage(job.hourlyWage) : getHourlyRange()}
          </p>
        </div>
      </div>

      {/* -------- Employment & Growth Section -------- */}
      <div className="grid gap-3 p-3 mb-2 bg-green-50 rounded-lg border border-gray-200 grid-cols-2 employment-section">
        <div>
          <p 
            className="text-xs font-medium uppercase text-zinc-700 mb-1 cursor-help"
            title="Current employment level for this occupation, Sumner County"
          >
            Current Employment
          </p>
                     <p 
             className="text-base font-semibold text-green-700 cursor-help" 
             title="Number of people currently employed in this occupation in Sumner County"
           >
             {job.employment ? Math.round(job.employment).toLocaleString() : 'Not available'}
           </p>
        </div>
        <div>
          <p 
            className="text-xs font-medium uppercase text-zinc-700 mb-1 cursor-help"
            title="Projected growth trend, Sumner County"
          >
            Growth Trend
          </p>
          <div className="flex items-center">
            {job.employmentPercentChange !== undefined && job.employmentPercentChange !== null ? (
              <>
                <svg 
                  className={`w-4 h-4 mr-2 ${job.employmentPercentChange >= 0 ? 'text-green-600' : 'text-red-600'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  {job.employmentPercentChange >= 0 ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"></path>
                  )}
                </svg>
                                 <span 
                                   className={`text-base font-semibold ${job.employmentPercentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                   title={getGrowthTooltip(job.employmentPercentChange, job.employment, job.projectedEmployment)}
                                 >
                   {formatGrowthPercentage(job.employmentPercentChange)}
                 </span>
              </>
            ) : (
              <span className="text-base font-semibold text-gray-500">Not available</span>
            )}
          </div>
        </div>
      </div>

      {/* -------- Job Openings Projection -------- */}
      <div className="mb-2">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h4 
              className="text-base font-semibold text-zinc-800 hover:underline cursor-pointer transition-colors"
              title="Click to view detailed employment projections and industry breakdown"
              onClick={(e) => {
                e.stopPropagation();
                // This could open a detailed view or modal
              }}
            >
              Job Openings Projection
            </h4>
            <p 
              className="text-xs text-zinc-600 cursor-help"
              title={`Employment projections from ${baseYear} to ${endYear}`}
            >
              {windowSize}-year window ({baseYear} - {endYear})
            </p>
          </div>
          <select
            value={windowSize}
            onChange={(e) => setWindowSize(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white cursor-help"
            onClick={(e) => e.stopPropagation()}
            title="Select time period for employment projections"
          >
            {[2, 3, 4, 6].map((size) => (
              <option key={size} value={size}>
                {size} years
              </option>
            ))}
          </select>
        </div>

        {/* Chart */}
        <div className="p-2 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-200 shadow-sm h-36 projections-chart">
          {hasJobData ? (
            <ResponsiveContainer width="100%" height={120}>
              <LineChart
                data={windowedData}
                margin={{ top: 10, right: 10, left: 2, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="year"
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 0, right: 0 }}
                  fontSize={14}
                  tick={{ fill: '#555', dy: 5 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  padding={{ top: 0, bottom: 0 }}
                  domain={['dataMin', 'dataMax']}
                  fontSize={16}
                  tick={{ fill: '#555' }}
                  width={50}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="jobs"
                  stroke="#1b2c5a"
                  strokeWidth={3}
                  dot={{ r: 3, fill: '#1b2c5a', stroke: '#fff', strokeWidth: 1 }}
                  activeDot={{ r: 5, fill: '#ff7300', stroke: '#ff7300', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-500">No job projection data available</p>
            </div>
          )}
        </div>

        <div className="mt-1 text-center">
          <p className="text-xs text-zinc-600">
            {hasJobData ? (
              `${start.year} - ${end.year}: ${start.jobs.toLocaleString()} → ${end.jobs.toLocaleString()} jobs`
            ) : (
              "No job projection data available"
            )}
          </p>
        </div>
      </div>

      {/* -------- Footer -------- */}
      <footer className="pt-2 border-t border-gray-200">
        <div className="px-4 py-3 text-center bg-green-500 rounded-lg">
          <p className="text-lg font-semibold text-white">
            {(job.latest365DaysPostings || job.uniquePosting)
              ? (job.latest365DaysPostings || job.uniquePosting)!.toLocaleString()
              : "N/A"}
          </p>
          <p className="text-xs font-medium text-white uppercase">
            Job Postings (Last 12 Months)
          </p>
        </div>
      </footer>
      
      {/* Mobile Bottom Sheet for Salary Explanation */}
      {showColTooltip && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={() => {
            setShowColTooltip(false);
            setIsTooltipOpen(false);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              width: '100%',
              maxWidth: '500px',
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              padding: '1.5rem',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <strong style={{ color: '#1b2c5a', fontSize: '1rem' }}>
                {showMedianSalary ? 'Cost of Living (COL) Salary' : 'Salary Range (10th–90th percentile)'}
              </strong>
              <button
                onClick={() => {
                  setShowColTooltip(false);
                  setIsTooltipOpen(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </div>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>
              {showMedianSalary 
                ? 'This number shows what a typical paycheck is worth in Sumner County after adjusting for local costs, so you can see how far your money really goes here compared to other places.'
                : 'This range shows salaries from the 10th percentile (lowest earners) to 90th percentile (highest earners) in Sumner County.'}
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

// Memoize JobCard to prevent unnecessary re-renders and performance violations
export default React.memo(JobCard);
